import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RestaurantDetailsService } from './restaurant-details.service';
import { LocalStorageService } from './localStorage.service';

export enum OrderSyncEvent {
  CART_UPDATED = 'CART_UPDATED',
  CART_CLEARED = 'CART_CLEARED',
  NEW_ORDER = 'NEW_ORDER',
  ORDER_UPDATED = 'ORDER_UPDATED',
  ORDER_COMPLETED = 'ORDER_COMPLETED',
  PAYMENT_INITIATED = 'PAYMENT_INITIATED',
  PAYMENT_COMPLETED = 'PAYMENT_COMPLETED',
  PAYMENT_CLOSED = 'PAYMENT_CLOSED',
  MERGE_BILL = 'MERGE_BILL',
  CAPTURE_CUSTOMER_DETAILS = 'CAPTURE_CUSTOMER_DETAILS',
  CAPTURE_CUSTOMER_DETAILS_CLOSED = 'CAPTURE_CUSTOMER_DETAILS_CLOSED',
  CUSTOMER_SKIP = 'CUSTOMER_SKIP',
}

/** Suppress passive getOrderDetailsNest ORDER_UPDATED / ORDER_COMPLETED for merge bill and settle. */
export const OrderSyncFetchSuppressMode = {
  MERGE_BILL: 'MERGE_BILL',
  SETTLE: 'SETTLE',
  SPLIT_BILL: 'SPLIT_BILL',
} as const;

export type OrderSyncFetchSuppressMode =
  (typeof OrderSyncFetchSuppressMode)[keyof typeof OrderSyncFetchSuppressMode];

/** Path key for set-status. Restaurant orders use `order`; Central Cashier uses `fc_order`. */
export type OrderSyncIdKey = 'order' | 'fc_order';

export interface MergeBillOrderSyncEntry {
  order_id: string;
  order_details: any;
}

export interface OrderSplitSyncEntry {
  guest_id: string;
  guest_details: any;
}

@Injectable({
  providedIn: 'root',
})
export class SyncService {
  private restaurantDetailsService: RestaurantDetailsService;

  constructor(
    private httpClient: HttpClient,
    private _localStorage: LocalStorageService,
    private _injector: Injector
  ) { }

  private syncUrl = environment.syncURL;
  private readonly DEVICE_ID = 1;
  /** Customer display screen shown for each order-sync event. Unlisted events send nothing. */
  private readonly customerDisplayScreens: Record<string, string> = {
    [OrderSyncEvent.CART_UPDATED]: 'cart',
    [OrderSyncEvent.NEW_ORDER]: 'cart',
    [OrderSyncEvent.ORDER_UPDATED]: 'cart',
    [OrderSyncEvent.MERGE_BILL]: 'cart',
    [OrderSyncEvent.PAYMENT_CLOSED]: 'cart',
    [OrderSyncEvent.PAYMENT_INITIATED]: 'pay',
    [OrderSyncEvent.PAYMENT_COMPLETED]: 'success',
    [OrderSyncEvent.ORDER_COMPLETED]: 'success',
    [OrderSyncEvent.CART_CLEARED]: 'idle',
  };
  private readonly fetchSuppressModes = new Set<OrderSyncFetchSuppressMode>();
  private splitBillContext: {
    orderId: string;
    orderSplit: OrderSplitSyncEntry[];
  } | null = null;

  enableFetchSuppressMode(mode: OrderSyncFetchSuppressMode): void {
    this.fetchSuppressModes.add(mode);
  }

  disableFetchSuppressMode(mode: OrderSyncFetchSuppressMode): void {
    this.fetchSuppressModes.delete(mode);
  }

  isSplitBillModeActive(): boolean {
    return this.fetchSuppressModes.has(OrderSyncFetchSuppressMode.SPLIT_BILL);
  }

  setSplitBillContext(
    orderId: string | number,
    guests: any[] | OrderSplitSyncEntry[]
  ): void {
    if (!orderId) {
      return;
    }
    const orderSplit =
      Array.isArray(guests) &&
      guests.length &&
      guests[0]?.guest_details !== undefined
        ? (guests as OrderSplitSyncEntry[])
        : this.buildOrderSplitFromGuests(guests as any[]);
    this.splitBillContext = {
      orderId: String(orderId),
      orderSplit,
    };
  }

  clearSplitBillContext(): void {
    this.splitBillContext = null;
  }

  getSplitBillOrderSplit(
    orderId: string | number
  ): OrderSplitSyncEntry[] | null {
    if (
      !this.splitBillContext ||
      this.splitBillContext.orderId !== String(orderId)
    ) {
      return null;
    }
    return this.splitBillContext.orderSplit;
  }

  shouldSkipOrderSyncFromFetch(): boolean {
    return (
      this.fetchSuppressModes.has(OrderSyncFetchSuppressMode.MERGE_BILL) ||
      this.fetchSuppressModes.has(OrderSyncFetchSuppressMode.SETTLE)
    );
  }

  /** Sync is active only when restaurant settings define display_app.enabled as true. */
  isSyncEnabled(): boolean {
    const settings = this.getRestaurantSettings();
    return settings?.display_app?.enabled === true;
  }

  /** Central Cashier (kiosk) orders are addressed by fc_order_id, not order_id. */
  isCentralCashier(): boolean {
    const settings = this.getRestaurantSettings();
    return !!settings?.global?.is_kiosk;
  }

  private getRestaurantDetailsService(): RestaurantDetailsService {
    if (!this.restaurantDetailsService) {
      this.restaurantDetailsService = this._injector.get(RestaurantDetailsService);
    }
    return this.restaurantDetailsService;
  }

  private getRestaurantSettings(): Record<string, any> | null {
    const details = this.getRestaurantDetailsService().getCurrentDetails();
    if (details?.settings) {
      return details.settings;
    }
    try {
      const stored = this._localStorage.get('restaurant_settings');
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  private get jsonHeaders() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }

  setOrderStatus(
    rest_id: string | number,
    order_id: string | number,
    body: { event: string; data: any },
    is_only_trigger: boolean = false,
    idKey?: OrderSyncIdKey
  ) {
    if (!this.isSyncEnabled()) {
      return of(null);
    }
    this.notifyCustomerDisplay(body);
    const device_id = localStorage.getItem('deviceId') || '';
    const params = new URLSearchParams({
      device_id,
      is_only_trigger: String(is_only_trigger),
    });
    const segment = this.resolveOrderSyncSegment(idKey);
    const url = `${this.syncUrl}/v1/order-sync/restaurant/${rest_id}/order/${order_id}/set-status/?${params.toString()}`;
    return this.httpClient
      .post<any>(url, body, { headers: this.jsonHeaders })
      .pipe(map((data) => data));
  }

  /** Restaurant: `/order/{order_id}`. Central Cashier: `/fc_order/{fc_order_id}`. */
  private resolveOrderSyncSegment(idKey?: OrderSyncIdKey): OrderSyncIdKey {
    if (idKey) {
      return idKey;
    }
    return this.isCentralCashier() ? 'fc_order' : 'order';
  }

  /** Mirrors order-sync events to the RN customer display. Browser windows are skipped. */
  private notifyCustomerDisplay(body: { event: string; data: any }): void {
    const webView = (typeof window !== 'undefined' && (window as any).ReactNativeWebView) || null;
    if (!webView || typeof webView.postMessage !== 'function') {
      return;
    }
    const screen = this.customerDisplayScreens[body?.event];
    if (!screen) {
      return;
    }
    const data = body.data || {};
    const orderDetail = data.order_details ?? data.cart ?? null;
    const orderBilling =
      data.order_billing ?? data.cart_bill ?? data.order_details?.bill ?? null;
    try {
      webView.postMessage(
        JSON.stringify({
          action: 'customer_display',
          screen,
          orderDetail,
          orderBilling,
        })
      );
    } catch (error) {
      console.error('Error sending customer_display to React Native:', error);
    }
  }

  syncCartState(
    rest_id: string,
    order_id: string,
    cart: any,
    cart_bill: any,
    idKey?: OrderSyncIdKey
  ) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.CART_UPDATED,
      data: { cart, cart_bill },
    }, false, idKey);
  }

  syncNewOrderState(rest_id: string, order_id: string, order_details: any, order_billing: any) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.NEW_ORDER,
      data: { order_details, order_billing },
    }, false);
  }

  syncOrderUpdateState(
    rest_id: string,
    order_id: string,
    order_details: any,
    order_billing: any,
    extras: { order_split?: OrderSplitSyncEntry[] } = {},
    idKey?: OrderSyncIdKey
  ) {
    const data: {
      order_details: any;
      order_billing: any;
      order_split?: OrderSplitSyncEntry[];
    } = { order_details, order_billing, ...extras };
    if (
      !data.order_split &&
      this.isSplitBillModeActive() &&
      this.splitBillContext?.orderId === String(order_id) &&
      this.splitBillContext.orderSplit.length
    ) {
      data.order_split = this.splitBillContext.orderSplit;
    }
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.ORDER_UPDATED,
      data,
    }, false, idKey);
  }

  syncOrderSplitUpdateState(
    rest_id: string | number,
    order_id: string | number,
    order_split: OrderSplitSyncEntry[],
    order_details?: any
  ) {
    this.setSplitBillContext(order_id, order_split);
    if (order_details) {
      return this.syncOrderUpdateState(
        String(rest_id),
        String(order_id),
        order_details,
        order_details?.bill,
        { order_split }
      );
    }
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.ORDER_UPDATED,
      data: { order_split },
    }, false);
  }

  buildOrderSplitFromGuests(guests: any[]): OrderSplitSyncEntry[] {
    return (guests || []).map((guest) => ({
      guest_id: String(guest?.guest_id ?? ''),
      guest_details: guest,
    }));
  }

  syncOrderSplitFromGuestsApi(
    rest_id: string | number,
    order_id: string | number,
    guests: any[],
    order_details?: any
  ) {
    if (!guests?.length) {
      return;
    }
    const order_split = this.buildOrderSplitFromGuests(guests);
    this.setSplitBillContext(order_id, order_split);
    return this.syncOrderSplitUpdateState(
      rest_id,
      order_id,
      order_split,
      order_details
    );
  }

  syncOrderCompleteState(
    rest_id: string,
    order_id: string,
    order_details: any,
    order_billing: any,
    idKey?: OrderSyncIdKey
  ) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.ORDER_COMPLETED,
      data: { order_details, order_billing },
    }, false, idKey);
  }

  setCustomerDetailsState(rest_id: string, order_id: string) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.CAPTURE_CUSTOMER_DETAILS,
      data: {},
    }, false);
  }

  closeCustomerDetailsState(rest_id: string, order_id: string) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.CAPTURE_CUSTOMER_DETAILS_CLOSED,
      data: {},
    }, false);
  }

  syncCustomerSkipState(rest_id: string, order_id: string, is_only_trigger: boolean = false) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.CUSTOMER_SKIP,
      data: {},
    }, is_only_trigger);
  }

  syncPaymentInitiateState(
    rest_id: string,
    order_id: string,
    data: {
      merged_orders?: MergeBillOrderSyncEntry[];
      payment_response?: any;
      order_details?: any;
      order_split?: OrderSplitSyncEntry[];
      selected_guest?: OrderSplitSyncEntry;
    } = {},
    idKey?: OrderSyncIdKey
  ) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.PAYMENT_INITIATED,
      data,
    }, false, idKey);
  }

  syncPaymentCompletedState(
    rest_id: string | number,
    order_id: string | number,
    data: {
      merged_orders?: MergeBillOrderSyncEntry[];
    } = {}
  ) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.PAYMENT_COMPLETED,
      data,
    }, false);
  }

  syncPaymentClosedState(rest_id: string, order_id: string) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.PAYMENT_CLOSED,
      data: {},
    }, false);
  }

  syncCartClearState(rest_id: string, order_id: string) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.CART_CLEARED,
      data: {},
    }, false);
  }

  syncMergeBillState(
    rest_id: string | number,
    order_id: string | number,
    merged_orders: MergeBillOrderSyncEntry[]
  ) {
    return this.setOrderStatus(rest_id, order_id, {
      event: OrderSyncEvent.MERGE_BILL,
      data: { merged_orders },
    }, false);
  }

}
